import { useState } from 'react';
import type { Chiffrage } from '../types';
import { updateStatusAndSendEmail } from '../services/api';
import './ActionModal.css';

interface ActionModalProps {
    chiffrage: Chiffrage;
    type: 'attribues' | 'partiel';
    onClose: () => void;
    onSuccess: () => void;
}

export function ActionModal({ chiffrage, type, onClose, onSuccess }: ActionModalProps) {
    const [numeroOrchestra, setNumeroOrchestra] = useState('');
    const [joursAttribues, setJoursAttribues] = useState<number>(0);
    const [lienJira, setLienJira] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const differentiel = type === 'partiel'
        ? Math.max(0, chiffrage.total_jours - joursAttribues)
        : 0;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!numeroOrchestra.trim()) {
            setError('Le numéro Orchestra est requis');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await updateStatusAndSendEmail(
                chiffrage.id,
                type === 'attribues' ? 'Attribués' : 'Partiel',
                numeroOrchestra.trim(),
                type === 'partiel' ? joursAttribues : undefined,
                undefined, // pas de fichier
                lienJira.trim() || undefined
            );

            onSuccess();
            onClose();
        } catch (err) {
            console.error('Erreur:', err);
            setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>×</button>

                <h2 className="modal-title">
                    {type === 'attribues' ? 'Jours demandés attribués' : 'Jours partiellement attribués'}
                </h2>

                <div className="modal-info">
                    <p><strong>Sujet:</strong> {chiffrage.sujet}</p>
                    <p><strong>Total demandé:</strong> {chiffrage.total_jours} jours</p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-field">
                        <label htmlFor="orchestra">Numéro de ligne Orchestra</label>
                        <input
                            id="orchestra"
                            type="text"
                            value={numeroOrchestra}
                            onChange={e => setNumeroOrchestra(e.target.value)}
                            placeholder="Ex: ORCH-2024-001"
                            required
                        />
                    </div>

                    <div className="modal-field">
                        <label htmlFor="lien-jira">Lien JIRA (optionnel)</label>
                        <input
                            id="lien-jira"
                            type="url"
                            value={lienJira}
                            onChange={e => setLienJira(e.target.value)}
                            placeholder="https://jira.malakoffhumanis.com/browse/XXX-123"
                        />
                    </div>

                    {type === 'partiel' && (
                        <>
                            <div className="modal-field">
                                <label htmlFor="jours">Nombre de jours attribués</label>
                                <input
                                    id="jours"
                                    type="number"
                                    min="0"
                                    max={chiffrage.total_jours}
                                    value={joursAttribues}
                                    onChange={e => setJoursAttribues(parseInt(e.target.value) || 0)}
                                    required
                                />
                            </div>

                            <div className="modal-differentiel">
                                <span className="diff-label">Différentiel (non attribué):</span>
                                <span className={`diff-value ${differentiel > 0 ? 'warning' : 'success'}`}>
                                    {differentiel} jour{differentiel !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </>
                    )}

                    {error && <div className="modal-error">{error}</div>}

                    <div className="modal-actions">
                        <button type="button" className="btn-cancel" onClick={onClose}>
                            Annuler
                        </button>
                        <button type="submit" className="btn-submit" disabled={loading}>
                            {loading ? 'Envoi...' : 'Valider et envoyer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
